import type { Battle } from "@loot-os/api-contract";
import { formatAmount } from "@loot-os/api-contract";
import { BattleOpener, BattleStartingOverlay, battleRoundRevealMs } from "@loot-os/react";
import { useEffect, useMemo, useRef } from "react";

import type { BattleMachineState } from "../../lib/battleMachine.js";
import { seatRunningTotals } from "../../lib/battleTimeline.js";
import { useCurrency } from "../../lib/currency.js";
import { seatLabel } from "../../lib/seatLabel.js";
import { serverNow } from "../../lib/serverClock.js";
import { SeatAvatar, SeatProfileTrigger } from "./SeatName.js";

/**
 * The battle's opener, from the countdown through the last round: `@loot-os/react`'s
 * `BattleOpener`, one seat column per lane, keyed by round so each round is a fresh mount. During
 * the countdown the first round's reels sit under the starting overlay (already generated when
 * the round's data is in, empty otherwise) and start moving the moment it clears. A seat whose
 * data has not arrived shows the empty reel; the reels start when the machine says `spinning`,
 * and the opener's completion moves it to `showing`. A safety timer covers reels whose
 * animations never complete (a throttled background tab).
 */
export function BattleLiveStage({
  battle,
  state,
  viewerEndUserId,
  countdownEndsAtMs,
  gateOpen,
  onCountdownFinished,
  onLaneSettled,
  onRoundAnimationFinished,
}: {
  battle: Battle;
  state: BattleMachineState;
  viewerEndUserId: string | null | undefined;
  /** The instant the first round starts, while the stage is the countdown. */
  countdownEndsAtMs: number | null;
  /** The current round's boundary has passed on the server clock (always true on replay). */
  gateOpen: boolean;
  onCountdownFinished: () => void;
  onLaneSettled: (seatIndex: number) => void;
  onRoundAnimationFinished: () => void;
}) {
  const currency = useCurrency();
  const isCountdown = state.stage === "countdown";
  const round = isCountdown ? 0 : state.round;
  const roundInfo = battle.rounds[round];
  const version = roundInfo
    ? battle.boxVersions.find((v) => v.id === roundInfo.boxVersionId)
    : undefined;
  const box = useMemo(
    () => (version ? { priceCents: version.priceCents, dropTable: version.dropTable } : null),
    [version],
  );
  // The round's opens reach the client a little before its boundary; hold them back until it.
  const opens = gateOpen ? battle.results[round] : undefined;
  const laneItems = useMemo(
    () =>
      battle.seats.map((seat) => opens?.find((o) => o.seatIndex === seat.seatIndex)?.item ?? null),
    [opens, battle.seats],
  );
  const running = state.stage === "live" && state.roundPhase === "spinning" && gateOpen;
  const totalsBefore = seatRunningTotals(battle, round - 1);

  // Safety: if the opener's completion never fires, move on when its choreography should have ended.
  const finishedRef = useRef(onRoundAnimationFinished);
  finishedRef.current = onRoundAnimationFinished;
  // biome-ignore lint/correctness/useExhaustiveDependencies: `round` re-arms the timer for each new round
  useEffect(() => {
    if (!running) return;
    const budget =
      battleRoundRevealMs({ laneCount: battle.playerCount, fastSpin: battle.fastSpin }) + 1500;
    const timer = setTimeout(() => finishedRef.current(), budget);
    return () => clearTimeout(timer);
  }, [running, round, battle.playerCount, battle.fastSpin]);

  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          Round {round + 1} of {battle.roundCount}
          {version ? ` · ${version.name}` : ""}
        </span>
        {state.replaying ? <span className="text-xs uppercase tracking-wide">Replay</span> : null}
      </div>
      <BattleOpener
        key={`${battle.id}:${round}:${state.replaying ? "replay" : "live"}`}
        box={box}
        laneItems={laneItems}
        seed={`${battle.id}:${round}`}
        running={running}
        quick={battle.fastSpin}
        invertSound={battle.mode === "crazy"}
        currency={currency}
        renderSeatHeader={(seatIndex, { compact, settled }) => {
          const seat = battle.seats[seatIndex];
          if (!seat) return null;
          const item = laneItems[seatIndex];
          const total = (totalsBefore[seatIndex] ?? 0) + (settled && item ? item.value : 0);
          return (
            <div className="flex w-full min-w-0 items-center justify-between gap-2">
              <SeatProfileTrigger
                seat={seat}
                viewerEndUserId={viewerEndUserId}
                className="flex min-w-0 items-center gap-1.5"
              >
                <SeatAvatar
                  seat={seat}
                  viewerEndUserId={viewerEndUserId}
                  size={compact ? 24 : 32}
                />
                {compact ? null : (
                  <span className="truncate text-sm font-semibold">
                    {seatLabel(seat, viewerEndUserId)}
                  </span>
                )}
              </SeatProfileTrigger>
              {compact ? (
                <span className="font-mono text-xs font-semibold">
                  {formatAmount(total, currency, { compact: true })}
                </span>
              ) : null}
            </div>
          );
        }}
        overlay={
          isCountdown && countdownEndsAtMs !== null ? (
            <BattleStartingOverlay
              endsAtMs={countdownEndsAtMs}
              now={serverNow}
              onFinished={onCountdownFinished}
            >
              {battle.seats.map((seat) => (
                <SeatAvatar
                  key={seat.seatIndex}
                  seat={seat}
                  viewerEndUserId={viewerEndUserId}
                  size={32}
                />
              ))}
            </BattleStartingOverlay>
          ) : null
        }
        onLaneSettled={onLaneSettled}
        onRevealComplete={onRoundAnimationFinished}
      />
    </div>
  );
}
